import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Tooltip } from 'antd';

import ItemFilterBuilder from './ItemFilterBuilder';
import ItemFilterChips from './ItemFilterChips';
import FilterDriftWarning from './FilterDriftWarning';
import QueryRefusalPick from './QueryRefusalPick';
import ItemsListToolbar from './ItemsListToolbar';
import ItemsListBody from './ItemsListBody';
import ItemsTable from './ItemsTable';
import ItemCardsGrid from './ItemCardsGrid';
import SortSelect from './SortSelect';
import ViewModeSwitch from './ViewModeSwitch';
import ListCount from './ListCount';
import ListSearchBox, { focusListSearch } from './ListSearchBox';
import NoItemsMatch from './NoItemsMatch';
import ListScrollLoader from './ListScrollLoader';
import { FilterByValueProvider } from '../common/FilterByValueContext';
import useListFilter from '../../hooks/useListFilter';
import useListSearch from '../../hooks/useListSearch';
import { listSearchAvailable } from '../../config/listSearch';
import useListSort, { useListSortOrder } from '../../hooks/useListSort';
import useViewMode from '../../hooks/useViewMode';
import useLinkedItems from '../../hooks/useLinkedItems';
import useTileLayouts from '../../hooks/useTileLayouts';
import useTableLayouts from '../../hooks/useTableLayouts';
import useCollectionTags from '../../hooks/useCollectionTags';
import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';
import { useCollectionContext } from '../../contexts/CollectionContext';
import { useOptionalTags } from '../../contexts/TagsContext';
import { PRIMARY_DATE_SORT } from '../../utils/cards';
import { RATE_LIMITED_TEXT } from '../../utils/feedMessages';
import { familyIdsOf } from '../../utils/collectionFamily';
import useApplyValueFilter from '../../hooks/useApplyValueFilter';
import { cardAndDescendantIds, createFacetResolver } from '../../utils/facets';
import { itemHomeCollectionId } from '../../utils/linkedRecords';
import { buildFilterFields, tagsFilterField } from '../../utils/kqlBuilder';
import { readStoredItemsViewMode } from '../../utils/itemsView';
import { normalizeId } from '../../utils/ids';
import { itemTagTitlesOf } from '../../utils/tagTitleMemory';
import { listStateSentence, withoutSearchTerm } from '../../utils/relatedListSwitcher';
import { UNSORTABLE_TYPES, sortElementOf, sortOf } from '../../utils/relatedLists';
import useWindowClass from '../../hooks/useWindowClass';

const EMPTY_CARDS = Object.freeze({});
const NO_TAGS = Object.freeze([]);
const noElements = () => [];
const defaultPrimaryDate = () => ({ kind: 'created' });

/**
 * What the filter row's count says, so it cannot be mistaken for the count on
 * the list's entry in the switcher: that one is the list under its DEFAULT
 * filter; this one is what is on screen, under the viewer's own.
 */
const COUNT_UNFILTERED = { singular: 'item', plural: 'items' };
const COUNT_FILTERED = { singular: 'matching your filter', plural: 'matching your filter' };
const COUNT_SEARCHED = { singular: 'matching your search', plural: 'matching your search' };
const COUNT_SEARCHED_FILTERED = { singular: 'matching your search and filter', plural: 'matching your search and filter' };
// The search term is the box's; its chip is hidden while the box shows it.
const SEARCH_PATHS = Object.freeze(['text']);
const NO_PATHS = Object.freeze([]);

/**
 * One related list, open (KPR-719, epic KPE-029): the items that link to the
 * viewed item, as the collection list shows items - the same filter, sort,
 * grid/table switch, rows and scroll loading, from the shared pieces
 * (KPR-736, KPR-737) - fed by `GET /api/items?related_item=&related_list=`.
 *
 *   <RelatedListView
 *     itemId collectionId
 *     list={{ id, name, count, state }}      // the switcher's entry for it
 *     definition={{ sourceCardId, … }}       // the card's served definition of it
 *     viewState={{ query, sort, dir, mode }} onViewState={(patch, { persist }) => …}
 *     onReset={() => …}
 *   />
 *
 * What the viewer chose (their filter, sort, grid/table) is the section's to
 * keep between visits to the list (`viewState`), and the section saves their
 * filter and grid/table choice to their account (KPR-722) - never the search
 * term (owner ruling t03): the term rides the list's query text while the list
 * is open, and `savableViewState` (utils/relatedListSwitcher) is the one door
 * the account save goes through. `persist: false` marks a change the person
 * did not make - the server's canonical spelling of their filter - which is
 * kept but not saved. `onReset` (when given) draws Reset while a filter or a
 * view is chosen: back to the default filter and view.
 *
 * THE LIST'S ORDER (KPR-493): a list whose card set it an order (`sort` on
 * its definition: an element of the source card, by id, and a direction)
 * opens in that order until the viewer chooses another. Until then the feed
 * sends NO sort at all and the server applies the list's own order (its
 * element as it is now, the default order among ties — or the default order
 * when the element is gone or hidden from the viewer), so the read never
 * waits on this page knowing the source card's elements; the element's name
 * (`listOrder`) is only what the sort menu shows. The rows are drawn in the
 * order the server sent them: a client re-sort of the page (reversed for a
 * descending order) would undo its tie-break. An inline edit that changes the
 * sorted value moves the row on the next read, not at once.
 *
 * The search box (KPR-740) is `useListSearch` over that same query text: it
 * writes the top-level `text ~ "term"` the feed sends as `q`, so the open
 * list's rows and its own count narrow with it. The switcher's counts are the
 * lists' DEFAULT filters' and never move with a search.
 */
export default function RelatedListView({
	itemId,
	collectionId,
	list,
	definition = null,
	viewState = null,
	onViewState,
	onReset = null,
	refreshKey = 0,
	onListsChanged,
	onRetryLists,
}) {
	const navigate = useNavigate();
	const { compact: isMobile } = useWindowClass();
	const cardDefinitions = useOptionalCardDefinitions();
	const cards = cardDefinitions?.cards;
	const cardsById = cardDefinitions?.cardsById || EMPTY_CARDS;
	const getElementsForCard = cardDefinitions?.getElementsForCard || noElements;
	const cardPrimaryDate = cardDefinitions?.cardPrimaryDate || defaultPrimaryDate;
	const elementSets = cardDefinitions?.elementSets;
	const cardsLoading = Boolean(cardDefinitions?.loading);
	const cardsVersion = cardDefinitions?.version || 0;

	// The collection the rows are read in: the viewed item's, when the page's
	// own collection is it or holds it (a parent's pane over a sub-collection's
	// item). Its sub-collections are what qualifies a row's badge; its access
	// is what row actions follow. Outside that, rows read as the API served
	// them, with no actions invented for a collection this surface lacks.
	const contextCollection = useCollectionContext()?.collection || null;
	const collection = contextCollection && familyIdsOf(contextCollection).has(normalizeId(collectionId)) ? contextCollection : null;

	// ---- What the viewer chose ----
	const query = viewState?.query || '';
	const chosenSort = viewState?.sort;
	const chosenDirection = viewState?.dir;
	// Set while the feed hook adopts the server's canonical text for the
	// person's filter (its `onAdopted` runs just before its `setQuery`): that
	// change is not the person's, so it is not saved to the account.
	const adoptingRef = useRef(false);
	const onAdopted = useCallback(() => { adoptingRef.current = true; }, []);
	const setQuery = useCallback((text) => {
		const next = text ? `${text}`.trim() : '';
		const persist = !adoptingRef.current;
		adoptingRef.current = false;
		onViewState({ query: next }, { persist });
	}, [onViewState]);
	const writeSort = useCallback((patch) => {
		if (patch.sort === undefined || patch.sort === null) onViewState({ sort: undefined, dir: undefined });
		else onViewState({ sort: patch.sort, dir: patch.dir === 'asc' ? 'asc' : 'desc' });
	}, [onViewState]);

	// Where the choice lives between visits to the list: the section's state,
	// seeded from the account (KPR-722). The collection list's own grid/table
	// choice seeds a list never chosen. (A store built every render is fine:
	// its id is what the hook reads by — a Reset gives it a new one, so the
	// hook reads the default again.)
	const [resets, setResets] = useState(0);
	const { mode: viewMode, setMode: changeViewMode } = useViewMode({
		store: {
			id: `related:${list.cardId || ''}:${list.id}:${resets}`,
			read: () => viewState?.mode || readStoredItemsViewMode(collectionId),
			write: mode => onViewState({ mode }),
		},
	});
	const view = viewMode === 'table' ? 'table' : 'grid';

	const { layouts: tileLayouts } = useTileLayouts(collectionId, cardsVersion);
	const { layouts: tableLayouts } = useTableLayouts(collectionId, cardsVersion, { enabled: view === 'table' });

	// The order the card set on the list, as the feed sends it (KPR-493).
	const definedOrder = sortOf(definition);
	const orderSourceId = normalizeId(definition?.sourceCardId);
	const listOrder = useMemo(() => {
		if (!definedOrder || !orderSourceId) return null;
		const element = sortElementOf(getElementsForCard(orderSourceId) || [], definedOrder.element);
		if (!element?.name || UNSORTABLE_TYPES.includes(element.dataType)) return null;
		return { field: element.name, direction: definedOrder.direction };
	}, [definedOrder?.element, definedOrder?.direction, orderSourceId, getElementsForCard]); // eslint-disable-line react-hooks/exhaustive-deps
	const sortOrder = useListSortOrder({
		field: chosenSort || listOrder?.field || PRIMARY_DATE_SORT,
		direction: chosenSort ? (chosenDirection === 'asc' ? 'asc' : 'desc') : (listOrder?.direction || 'desc'),
		chosen: Boolean(chosenSort),
		getElementsForCard,
	});

	// ---- The search box: the top-level `text ~` of the viewer's query ----
	const search = useListSearch({ query, setQuery });
	// The gate (owner ruling t05, config/listSearch.js): the list's own size
	// is the entry's count under its default filter. Unknown shows the box.
	const searchAvailable = !list.state && listSearchAvailable(list.count);
	const searchId = `related-list-search-${list.id}`;

	// ---- The feed: the viewed item's list, under the viewer's filter ----
	const extraParams = useMemo(() => ({ related_item: itemId, related_list: list.id }), [itemId, list.id]);
	const {
		items,
		totalCount,
		loading: itemsLoading,
		loadingMore,
		hasMore,
		loadError,
		landedPages,
		queryWarnings,
		queryWarningsOmitted,
		loadMore,
		retry,
		refresh,
		removeItem,
		replaceItem,
		queryRefusal,
		pickForRefusal,
		repick,
		removedIds,
	} = useListFilter({
		query,
		setQuery,
		onSearchRefused: search.onRefused,
		collectionId,
		// The server's order (the list's own) unless the viewer chose one.
		sortField: definedOrder && !chosenSort ? undefined : sortOrder.sortField,
		sortDirection: definedOrder && !chosenSort ? undefined : sortOrder.sortDirection,
		enabled: Boolean(itemId),
		extraParams,
		onAdopted,
	});

	// Something changed elsewhere in the item (a relation made or removed): read again.
	const firstRefreshKey = useRef(refreshKey);
	useEffect(() => {
		if (firstRefreshKey.current === refreshKey) return;
		firstRefreshKey.current = refreshKey;
		refresh();
	}, [refreshKey, refresh]);

	// ---- The filter's vocabulary: the source card and the cards beneath it ----
	// (The server pins the filter's names to the same cards.)
	const sourceCardId = normalizeId(definition?.sourceCardId);
	const familyCardIds = useMemo(
		() => new Set(sourceCardId ? cardAndDescendantIds(cards || [], sourceCardId) : []),
		[cards, sourceCardId]
	);
	const { tags: tagVocabulary, loaded: tagVocabularyLoaded } = useCollectionTags(collectionId);
	const myPrivateTags = useOptionalTags()?.tags || NO_TAGS;
	const feedItemTagTitles = useMemo(() => itemTagTitlesOf(items), [items]);
	const tagsField = useMemo(() => tagsFilterField({
		tags: tagVocabulary,
		privateTags: myPrivateTags,
		itemTitles: feedItemTagTitles,
		mode: 'live',
		loaded: tagVocabularyLoaded,
		quickFilter: false,
	}), [tagVocabulary, myPrivateTags, feedItemTagTitles, tagVocabularyLoaded]);
	const filterFields = useMemo(() => [
		...buildFilterFields({ cards, cardIds: familyCardIds, getElementsForCard, elementSets }),
		tagsField,
	], [cards, familyCardIds, getElementsForCard, elementSets, tagsField]);
	const facetFor = useMemo(() => createFacetResolver(filterFields), [filterFields]);
	// "Filter by this value" on a row filters THIS list: the page behind the
	// pane has its own filter, and a click here must not move it.
	const applyValueFilter = useApplyValueFilter({ query, setQuery });
	const resolveFilterCard = useCallback(value => cardsById?.[normalizeId(`${value || ''}`)] || null, [cardsById]);

	// `/` focuses this list's search box (platform-standards section 5). A page
	// that has its own `/` (the collection list's, behind the pane) answers
	// first - it is registered earlier and says so with `preventDefault` - so
	// the key only comes here on a page with no list of its own: the item page.
	useEffect(() => {
		if (!searchAvailable) return undefined;
		const handler = (event) => {
			if (event.key !== '/' || event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return;
			const target = event.target;
			if (target instanceof Element && target.closest('input, textarea, select, [contenteditable="true"], .ant-modal, .ant-popover, .ant-drawer, .ant-select')) return;
			if (focusListSearch(searchId)) event.preventDefault();
		};
		document.addEventListener('keydown', handler);
		return () => document.removeEventListener('keydown', handler);
	}, [searchAvailable, searchId]);

	const triggerId = `related-list-filter-${list.id}`;
	const openFilterBuilder = useCallback(() => {
		document.getElementById(triggerId)?.click();
	}, [triggerId]);

	const sort = useListSort({
		order: sortOrder,
		writeSort,
		cardId: sourceCardId || undefined,
		items,
		getElementsForCard,
		cardPrimaryDate,
	});

	const itemsById = useLinkedItems({
		items,
		getElementsForCard,
		enabled: !cardsLoading,
		resetKey: `${itemId}:${list.id}`,
	});

	const openItem = useCallback((selected) => {
		if (!selected) return;
		const id = normalizeId(selected._id);
		if (!id) return;
		navigate(`/collections/${itemHomeCollectionId(selected, collectionId)}/items/${id}`);
	}, [navigate, collectionId]);
	const handleDeleted = useCallback((id) => {
		removeItem(id);
		if (onListsChanged) onListsChanged();
	}, [removeItem, onListsChanged]);

	const firstPageFailed = loadError?.page === 0;
	const laterPageFailed = Boolean(loadError) && !firstPageFailed;
	const isLoading = itemsLoading || cardsLoading;
	const filtered = Boolean(query);

	const listProps = {
		cardsById,
		tileLayouts,
		resolvePrimaryDate: cardPrimaryDate,
		itemsById,
		collectionId,
		collection,
		getElementsForCard,
		selectedItemId: null,
		onSelectItem: openItem,
		onItemDeleted: handleDeleted,
		onItemUpdated: replaceItem,
	};

	const retryAll = useCallback(() => {
		retry();
		// The list may be the thing that changed (renamed, removed): read the
		// switcher's lists again beside the rows.
		if (onRetryLists) onRetryLists();
	}, [retry, onRetryLists]);

	// ---- The toolbar's pieces ----
	const filterChips = query ? (
		<ItemFilterChips
			query={query}
			fields={filterFields}
			onChange={setQuery}
			className='items-page__filter-chips'
			resolveCard={resolveFilterCard}
			removedIds={removedIds}
			hiddenPaths={searchAvailable ? SEARCH_PATHS : NO_PATHS}
		/>
	) : null;
	const searchBox = searchAvailable ? (
		<ListSearchBox search={search} id={searchId} />
	) : null;
	const filterBuilder = (
		<ItemFilterBuilder
			fields={filterFields}
			query={query}
			removedIds={removedIds}
			onApply={setQuery}
			collectionId={collectionId}
			scopeCardIds={familyCardIds}
			triggerId={triggerId}
			emptyText='No conditions yet — every item in this list is shown.'
		/>
	);
	const sortSelect = <SortSelect {...sort.selectProps} />;
	const countWords = search.applied
		? (search.otherFilters ? COUNT_SEARCHED_FILTERED : COUNT_SEARCHED)
		: (filtered ? COUNT_FILTERED : COUNT_UNFILTERED);
	const countLabel = (
		<ListCount
			count={totalCount}
			loading={itemsLoading}
			failed={firstPageFailed}
			{...countWords}
		/>
	);
	// Reset (KPR-722): only while the viewer has chosen something.
	// A search alone is not something to reset: the box clears its own term.
	const canReset = Boolean(onReset) && Boolean(withoutSearchTerm(query) || viewState?.mode);
	const handleReset = useCallback(() => {
		if (!onReset) return;
		onReset();
		setResets(n => n + 1);
		// Reset goes away once used: keep the keyboard where the next step is.
		window.requestAnimationFrame(() => document.getElementById(triggerId)?.focus());
	}, [onReset, triggerId]);
	const resetButton = canReset ? (
		<Tooltip title='Back to this list’s default filter and view'>
			<Button type='link' className='related-list__reset' onClick={handleReset}>Reset</Button>
		</Tooltip>
	) : null;
	const viewSwitch = (
		<ViewModeSwitch value={view} onChange={changeViewMode} isMobile={isMobile} busy={itemsLoading} />
	);

	// Why the list is empty, in one line that says what to do next.
	const empty = search.applied ? (
		<NoItemsMatch
			className='related-list__empty'
			term={search.applied}
			otherFilters={search.otherFilters}
			onClearSearch={search.clear}
			onClearFilters={() => setQuery(search.termOnlyQuery)}
		/>
	) : filtered ? (
		<div className='related-list__empty'>
			<span>No items match your filter.</span>
			<Button type='link' onClick={() => setQuery(undefined)}>Clear filter</Button>
		</div>
	) : (
		<div className='related-list__empty'>
			<span>{listStateSentence(list.state) || 'Nothing links here yet. An item appears when it looks up this one in a lookup this list reads.'}</span>
		</div>
	);

	// The server's order (see THE LIST'S ORDER above), never a re-sort of the page.
	const body = view === 'table' ? (
		<ItemsTable {...listProps} items={items} loading={false} tableLayouts={tableLayouts} onReload={refresh} />
	) : (
		<ItemCardsGrid {...listProps} items={items} loading={false} emptyText={null} />
	);

	const failure = firstPageFailed
		? (loadError.kind === 'rate_limited' ? RATE_LIMITED_TEXT : true)
		: null;

	return (
		<FilterByValueProvider applyValueFilter={applyValueFilter} facetFor={facetFor}>
			<div className='related-list' data-testid='related-list'>
				<ItemsListToolbar
					stacked={isMobile}
					search={searchBox}
					primary={isMobile ? viewSwitch : null}
					secondary={isMobile ? (
						<>
							{filterBuilder}
							{sortSelect}
							{resetButton}
							{countLabel}
						</>
					) : null}
					chips={isMobile && filterChips ? filterChips : null}
				>
					{isMobile ? null : (
						<>
							{filterChips}
							{filterBuilder}
							{resetButton}
							<span className='items-page__toolbar-divider' aria-hidden='true' />
							{sortSelect}
							<span className='items-page__toolbar-spacer' />
							{countLabel}
							{viewSwitch}
						</>
					)}
				</ItemsListToolbar>

				<QueryRefusalPick
					refusal={queryRefusal}
					onPick={pickForRefusal}
					onRemove={() => setQuery(undefined)}
					onRepick={repick}
				/>
				<FilterDriftWarning
					warnings={queryWarnings}
					omitted={queryWarningsOmitted}
					query={query}
					fields={filterFields}
					resolveCard={resolveFilterCard}
					onEditFilter={openFilterBuilder}
				/>

				<ItemsListBody
					view={view}
					items={items}
					loading={isLoading}
					error={failure}
					onRetry={retryAll}
					empty={empty}
					label={list.name ? `${list.name}` : 'items'}
				>
					{body}
				</ItemsListBody>

				<ListScrollLoader
					hasMore={hasMore && !firstPageFailed}
					loadMore={loadMore}
					loadingMore={loadingMore}
					landedPages={landedPages}
					error={laterPageFailed ? loadError : null}
					onRetry={retry}
					rearmKey={view}
				/>
			</div>
		</FilterByValueProvider>
	);
}
